import React, { useState } from 'react';
import Accordion from '../../common/Accordion';
import {
  copyTemplate,
  createTemplate,
  createTemplateRole,
  destroyTemplate,
  destroyTemplateRole,
  updateTemplate,
  updateTemplateRole,
} from '../constants/handleTemplates';
import Icon from '../../common/Icon';
import ContainerPermission from '../ContainerPermission';
import ContainerFilterButtons from './ContainerFilterButtons';

const ManageTemplates = ({ templates, containers, updateTemplateState }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editing, setEditing] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [filter, setFilter] = useState('active');
  const isSelected = (id) => selectedTemplate && selectedTemplate.id == id;
  const templateRoles = selectedTemplate
    ? templates.find((t) => t.id == selectedTemplate.id).template_roles
    : [];

  const determineContainerDisplay = () => {
    let templateContainerIds = templateRoles.map((r) => r.container_id);
    if (filter === 'all') {
      return containers;
    } else if (filter === 'active') {
      return containers.filter((c) => c.active);
    } else if (filter === 'roles') {
      return containers.filter((c) => templateContainerIds.includes(c.id));
    }
    return containers;
  };

  const displayContainers = determineContainerDisplay();

  const handleCreateTemplate = async () => {
    if (!newTemplateName) return;
    let created = await createTemplate({ name: newTemplateName });
    if (created.ok) {
      setNewTemplateName('');
      updateTemplateState([...templates, created.data]);
    }
  };

  const handleUpdateName = async () => {
    if (!selectedTemplate) return;

    let original = templates.find((t) => t.id == selectedTemplate.id);
    if (original.name == selectedTemplate.name) return setEditing(false);

    let updated = await updateTemplate(selectedTemplate);
    if (updated.ok) {
      let filtered = templates.filter((t) => t.id != selectedTemplate.id);
      updateTemplateState([...filtered, updated.data]);
      setEditing(false);
    }
  };

  const handleTemplateCopy = async (e, id) => {
    e.stopPropagation();
    await copyTemplate(id, (response) => {
      if (response.ok) updateTemplateState([...templates, response.data]);
    });
  };

  const handleDestroyTemplate = async (e, id) => {
    e.stopPropagation();
    await destroyTemplate(id, (response) => {
      if (response.ok)
        updateTemplateState([...templates.filter((t) => t.id != id)]);
      setSelectedTemplate('');
    });
  };

  const handleCreateTemplateRole = async (container_id) => {
    if (!selectedTemplate) return;
    let tr = { template_id: selectedTemplate.id, container_id, role: 0 };
    let created = await createTemplateRole(tr);
    if (created.ok) {
      let filtered = templates.filter((t) => t.id != selectedTemplate.id);
      let template = { ...templates.find((t) => t.id == selectedTemplate.id) };
      template.template_roles.push(created.data);
      updateTemplateState([...filtered, template]);
    }
  };

  const handleUpdateTemplateRole = async (id, permission) => {
    let template_role = { role: permission };
    let updated = await updateTemplateRole(id, template_role);
    if (updated.ok) {
      let roles = [...templateRoles.filter((r) => r.id != id), updated.data];
      let template = { ...templates.find((t) => t.id == selectedTemplate.id) };
      template.template_roles = roles;
      updateTemplateState([
        ...templates.filter((t) => t.id != template.id),
        template,
      ]);
    }
  };

  const handleDestroyTemplateRole = async (id) => {
    let deleted = await destroyTemplateRole(id);
    if (deleted) {
      let roles = [...templateRoles.filter((r) => r.id != id)];
      let template = { ...templates.find((t) => t.id == selectedTemplate.id) };
      template.template_roles = roles;
      updateTemplateState([
        ...templates.filter((t) => t.id != template.id),
        template,
      ]);
    }
  };

  return (
    <section className='templates'>
      <h2>Manage Templates</h2>
      <Accordion expanded={expanded} toggle={() => setExpanded(!expanded)}>
        <div className='flex'>
          <div className='manage-template'>
            <div className='row'>
              <label>Create New Template: </label>
            </div>
            <div className='row'>
              <div className='input-left'>
                <input
                  type='text'
                  placeholder='Ex: Readiness Basic'
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key == 'Enter') handleCreateTemplate();
                  }}
                />
              </div>
              <div className='small'>
                <button
                  onClick={() => handleCreateTemplate()}
                  disabled={!newTemplateName.length}
                >
                  Create
                </button>
              </div>
            </div>
            <div className='row'>
              <label>Edit Selected:</label>
            </div>
            <div className='row'>
              <div className='input-left'>
                {selectedTemplate && editing ? (
                  <>
                    <input
                      type='text'
                      value={selectedTemplate?.name}
                      onChange={(e) =>
                        setSelectedTemplate({
                          ...selectedTemplate,
                          name: e.target.value,
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key == 'Enter') {
                          handleUpdateName();
                        }
                      }}
                      autoFocus
                    />
                  </>
                ) : (
                  <input
                    type='text'
                    disabled
                    value={selectedTemplate?.name || 'None...'}
                  />
                )}
              </div>
              <div className='controls pad'>
                {selectedTemplate && (
                  <>
                    {editing ? (
                      <Icon
                        icon={'xmark'}
                        onClick={() => {
                          setEditing(false);
                          setSelectedTemplate(
                            templates.find((t) => t.id == selectedTemplate.id)
                          );
                        }}
                      />
                    ) : (
                      <Icon
                        icon={'pencil'}
                        className={'edit'}
                        onClick={() => setEditing(true)}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className='template-list'>
            <label>Template Select</label>
            <ul>
              {templates.map((template) => (
                <li
                  key={`template-${template.id}`}
                  onClick={() => {
                    setSelectedTemplate(template);
                    setEditing(false);
                  }}
                >
                  <div
                    className={
                      'content pad' +
                      (isSelected(template.id) ? ' is-selected' : '')
                    }
                  >
                    {template.name}&nbsp;
                  </div>
                  <div className='controls pad no-pointer'>
                    <Icon
                      icon='copy'
                      className='copy'
                      faPrefix={'regular'}
                      onClick={(e) => handleTemplateCopy(e, template.id)}
                    />
                    <Icon
                      icon='trash'
                      className='destroy'
                      onClick={(e) => handleDestroyTemplate(e, template.id)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <hr />

        <div className='user-roles'>
          <h3>Manage Template Roles</h3>
          <div className='row'>
            <label className='name'>Viewing:</label>
            <div className='controls'>
              <ContainerFilterButtons
                disabled={!selectedTemplate}
                filter={filter}
                setFilter={setFilter}
              />
            </div>
          </div>
          {selectedTemplate ? (
            <ul>
              {displayContainers.map((container) => (
                <li
                  key={`container-${container.id}`}
                  className={container.active ? 'pad' : 'pad inactive'}
                >
                  {templateRoles
                    .map((r) => r.container_id)
                    .includes(container.id) ? (
                    <div>
                      <div className='name'>{container.name}</div>
                      <ContainerPermission
                        role={templateRoles.find(
                          (r) => r.container_id == container.id
                        )}
                        updateRole={handleUpdateTemplateRole}
                        destroyRole={handleDestroyTemplateRole}
                      />
                    </div>
                  ) : (
                    <div>
                      <div className='name'>{container.name}</div>
                      <button
                        className='add-container-btn'
                        onClick={(e) => handleCreateTemplateRole(container.id)}
                        title='Add container to template'
                      >
                        <Icon icon='plus' />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>Select or Create Template</p>
          )}
        </div>
      </Accordion>
    </section>
  );
};

export default ManageTemplates;
